import React, { useEffect, useMemo, useState } from "react";

const CONTROL_ROLES = ["super_admin", "system_manager", "company_admin", "admin", "editor"];
const API = process.env.REACT_APP_API_URL || "/api";
const norm = (value) => String(value ?? "").trim().toLowerCase();

const getConfiguredRelays = (relays = []) => {
  const seen = new Set();
  return relays.map((relay, index) => ({ ...relay, key: relay?.key || relay?.relay || relay?.channel || ("i" + (index + 1)) }))
    .filter((relay) => {
      const key = norm(relay.key);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const readLiveState = (liveData, key) => {
  const raw = liveData?.[key] ?? liveData?.[norm(key)] ?? liveData?.[String(key).toUpperCase()];
  return raw === 1 || raw === true || raw === "1" || norm(raw) === "true" || norm(raw) === "on";
};

const toInputDateTime = (value) => {
  const date = value ? new Date(value) : new Date();
  return new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString().slice(0, 16);
};

const newDraft = (relays = []) => {
  const now = new Date();
  return { name: "", relays, mode: "always_on", intervalMinutes: 15, startAt: toInputDateTime(now), endAt: toInputDateTime(new Date(now.getTime() + (60 * 60 * 1000))), repeatMode: "daily" };
};

const modeLabel = (mode) => ({ always_on: "Always On", periodic: "Periodic", time_range: "Time Range" }[mode] || "Time Range");

const programDescription = (program, relayNames) => {
  const relay = (program.relays || []).map((key) => relayNames[norm(key)] || key).join(", ");
  if (program.mode === "always_on") return relay + " - stays ON and is restored after restart.";
  if (program.mode === "periodic") return relay + " - ON/OFF every " + program.interval_minutes + " minutes from " + new Date(program.start_at).toLocaleString() + ".";
  const repeat = program.repeat_mode === "none" ? "Run once" : program.repeat_mode === "daily" ? "Every day" : "Every week";
  return relay + " - " + repeat + ", " + new Date(program.start_at).toLocaleString() + " to " + new Date(program.end_at).toLocaleString() + ".";
};

const configuredRuleDescription = (rule, relayNames) => {
  const relay = relayNames[norm(rule.key)] || rule.label || rule.key;
  const mode = norm(rule.mode) === "timer" ? "schedule" : norm(rule.mode);
  const window = rule.schedule_start_time && rule.schedule_end_time
    ? `${rule.schedule_start_time}–${rule.schedule_end_time}`
    : "all configured hours";
  if (mode === "auto") return `${relay} — Auto: ${rule.trigger_sensor || "sensor"} ${rule.trigger_condition || ">"} ${rule.trigger_value ?? "?"}; ${window}.`;
  return `${relay} — Schedule: ${window}.`;
};

const RelayCard = ({ relay, index, isOn, isLoading, canControl, onToggle }) => {
  const isManual = norm(relay.mode || "manual") === "manual";
  const disabled = !canControl || isLoading || !isManual;
  return (
    <div className={"flex flex-col gap-4 bg-white rounded-xl border p-5 " + (isOn ? "border-emerald-200" : "border-gray-100")}>
      <div className="flex items-center justify-between gap-3">
        <div><p className="text-[11px] font-bold text-gray-400 uppercase">{relay.key}</p><p className="text-[13px] font-semibold text-gray-800">{relay.label || ("Relay " + (index + 1))}</p></div>
        <span className={"text-[11px] font-bold " + (isOn ? "text-emerald-600" : "text-gray-400")}>{isOn ? "ON" : "OFF"}</span>
      </div>
      <button disabled={disabled} onClick={() => onToggle(relay, isOn)} className={"w-full py-2 rounded-lg text-[12px] font-semibold " + (isOn ? "bg-red-600 text-white" : "bg-emerald-600 text-white") + (disabled ? " cursor-not-allowed opacity-50" : "")}>{isLoading ? "Sending..." : !isManual ? "Controlled by program" : isOn ? "Turn Off" : "Turn On"}</button>
    </div>
  );
};

export default function RelayControlTab({ device = {}, relays = [], liveData = {}, onControl }) {
  const relayList = useMemo(() => getConfiguredRelays(relays), [relays]);
  const relayNames = useMemo(() => Object.fromEntries(relayList.map((relay) => [norm(relay.key), relay.label || relay.key])), [relayList]);
  const [view, setView] = useState("manual");
  const [loadingRelay, setLoadingRelay] = useState(null);
  const [notice, setNotice] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [automation, setAutomation] = useState({ configuredRelays: [], states: [] });
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [savingProgram, setSavingProgram] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [draft, setDraft] = useState(() => newDraft());
  const user = (() => { try { return JSON.parse(localStorage.getItem("user") || "{}"); } catch { return {}; } })();
  const canControl = CONTROL_ROLES.includes(norm(user?.role));

  useEffect(() => {
    if (!device?.device_uid) return;
    let cancelled = false;
    setLoadingPrograms(true);
    fetch(API + "/devices/uid/" + encodeURIComponent(device.device_uid) + "/relay-automation", { headers: { Authorization: "Bearer " + localStorage.getItem("token") } })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.message || "Unable to load relay automation");
        if (!cancelled) {
          setPrograms(Array.isArray(data.programs) ? data.programs : []);
          setAutomation({ configuredRelays: data.configuredRelays || [], states: data.states || [] });
        }
      })
      .catch((error) => !cancelled && setNotice({ type: "error", text: error.message || "Unable to load relay automation" }))
      .finally(() => !cancelled && setLoadingPrograms(false));
    return () => { cancelled = true; };
  }, [device?.device_uid]);

  useEffect(() => {
    setDraft((current) => current.relays.length || !relayList.length ? current : { ...current, relays: [relayList[0].key] });
  }, [relayList]);

  const toggleProgramRelay = (relayKey) => {
    setDraft((current) => ({
      ...current,
      relays: current.relays.includes(relayKey)
        ? current.relays.filter((key) => key !== relayKey)
        : [...current.relays, relayKey],
    }));
  };

  const sendRelay = async (relay, state) => {
    if (!onControl) throw new Error("Relay control is unavailable");
    await onControl(relay, state ? 1 : 0);
  };

  const handleToggle = async (relay, currentState) => {
    if (!canControl || norm(relay.mode || "manual") !== "manual") return;
    setLoadingRelay(relay.key);
    try {
      await sendRelay(relay.key, !currentState);
      setNotice({ type: "success", text: "Relay command accepted. Waiting for device confirmation." });
    } catch (error) {
      setNotice({ type: "error", text: error.message || "Relay command failed" });
    } finally {
      setLoadingRelay(null);
    }
  };

  const addProgram = async () => {
    if (!draft.name.trim() || !draft.relays.length) {
      setNotice({ type: "error", text: "Enter a name and choose at least one relay." });
      return;
    }
    setSavingProgram(true);
    try {
      const body = { ...draft, startAt: draft.startAt ? new Date(draft.startAt).toISOString() : null, endAt: draft.endAt ? new Date(draft.endAt).toISOString() : null };
      const response = await fetch(API + "/devices/uid/" + encodeURIComponent(device.device_uid) + "/relay-programs", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + localStorage.getItem("token") },
        body: JSON.stringify(body),
      });
      const program = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(program.message || "Unable to save relay program");
      setPrograms((current) => [program, ...current]);
      setDraft(newDraft(draft.relays));
      setNotice({ type: "success", text: "Program saved. The server will restore and enforce it after restart." });
    } catch (error) {
      setNotice({ type: "error", text: error.message || "Unable to save relay program" });
    } finally {
      setSavingProgram(false);
    }
  };

  const setProgramEnabled = async (program, enabled) => {
    try {
      const response = await fetch(API + "/devices/uid/" + encodeURIComponent(device.device_uid) + "/relay-programs/" + program.id, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + localStorage.getItem("token") },
        body: JSON.stringify({ ...program, enabled, startAt: program.start_at, endAt: program.end_at, intervalMinutes: program.interval_minutes, repeatMode: program.repeat_mode }),
      });
      const saved = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(saved.message || "Unable to update relay program");
      setPrograms((current) => current.map((item) => item.id === program.id ? saved : item));
    } catch (error) {
      setNotice({ type: "error", text: error.message || "Unable to update relay program" });
    }
  };

  const deleteProgram = async (program) => {
    if (!window.confirm("Delete program \"" + program.name + "\"?")) return;
    try {
      const response = await fetch(API + "/devices/uid/" + encodeURIComponent(device.device_uid) + "/relay-programs/" + program.id, { method: "DELETE", headers: { Authorization: "Bearer " + localStorage.getItem("token") } });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.message || "Unable to delete relay program");
      setPrograms((current) => current.filter((item) => item.id !== program.id));
      setNotice({ type: "success", text: "Program deleted." });
    } catch (error) {
      setNotice({ type: "error", text: error.message || "Unable to delete relay program" });
    }
  };

  const resetSavedCommands = async () => {
    if (!window.confirm("Reset all saved programs and persistent relay commands for this device? Product Auto/Schedule rules will remain and are listed separately.")) return;
    setResetting(true);
    try {
      const response = await fetch(API + "/devices/uid/" + encodeURIComponent(device.device_uid) + "/relay-automation/reset", {
        method: "POST",
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.message || "Unable to reset saved relay commands");
      setPrograms([]);
      setAutomation((current) => ({ ...current, states: [] }));
      setNotice({ type: "success", text: `Cleared ${result.programsCleared || 0} program(s) and ${result.statesCleared || 0} persistent command(s). Product automation is unchanged.` });
    } catch (error) {
      setNotice({ type: "error", text: error.message || "Unable to reset saved relay commands" });
    } finally {
      setResetting(false);
    }
  };

  if (!relayList.length) return <div className="bg-white border rounded-xl p-12 text-center text-[13px] text-gray-400">No relays configured for this device</div>;

  return (
    <div className="space-y-5">
      {notice && <div className={"rounded-lg border px-4 py-3 text-[12px] font-medium " + (notice.type === "error" ? "bg-red-50 border-red-200 text-red-700" : "bg-emerald-50 border-emerald-200 text-emerald-700")}>{notice.text}</div>}
      <div className="flex items-center justify-between gap-3">
        <div className="flex gap-2">
          <button onClick={() => setView("manual")} className={"px-3 py-2 rounded-lg text-[12px] font-semibold " + (view === "manual" ? "bg-blue-600 text-white" : "bg-white border text-gray-600")}>Manual</button>
          <button onClick={() => setView("programs")} className={"px-3 py-2 rounded-lg text-[12px] font-semibold " + (view === "programs" ? "bg-blue-600 text-white" : "bg-white border text-gray-600")}>Programs</button>
        </div>
        <span className="text-[11px] text-gray-400">{relayList.filter((relay) => readLiveState(liveData, relay.key)).length} relays currently ON</span>
      </div>

      {view === "manual" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          {relayList.map((relay, index) => <RelayCard key={relay.key} relay={relay} index={index} isOn={readLiveState(liveData, relay.key)} isLoading={loadingRelay === relay.key} canControl={canControl} onToggle={handleToggle} />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-[360px_1fr] gap-4">
          <div className="bg-white border rounded-xl p-5 space-y-4">
            <h3 className="text-[14px] font-semibold text-gray-800">Create relay program</h3>
            <input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} placeholder="Program name" className="w-full border rounded-lg px-3 py-2 text-[12px]" />
            <fieldset className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <legend className="text-[11px] font-semibold text-gray-700">Relays controlled by this program</legend>
                <button type="button" onClick={() => setDraft({ ...draft, relays: draft.relays.length === relayList.length ? [] : relayList.map((relay) => relay.key) })} className="text-[11px] font-semibold text-blue-600">
                  {draft.relays.length === relayList.length ? "Clear all" : "Select all"}
                </button>
              </div>
              <div className="max-h-48 space-y-1 overflow-y-auto rounded-lg border p-2">
                {relayList.map((relay) => (
                  <label key={relay.key} className="flex min-h-10 cursor-pointer items-center gap-3 rounded-md px-2 py-2 hover:bg-gray-50">
                    <input type="checkbox" checked={draft.relays.includes(relay.key)} onChange={() => toggleProgramRelay(relay.key)} className="h-4 w-4 accent-blue-600" />
                    <span className="min-w-0 flex-1 text-[12px] font-medium text-gray-700">{relay.label || relay.key}</span>
                    <span className="text-[10px] font-semibold uppercase text-gray-400">{relay.key}</span>
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-gray-500">{draft.relays.length} relay{draft.relays.length === 1 ? "" : "s"} selected</p>
            </fieldset>
            <div className="grid grid-cols-3 gap-2">{["always_on", "periodic", "time_range"].map((mode) => <button key={mode} onClick={() => setDraft({ ...draft, mode })} className={"rounded-lg border px-2 py-2 text-[11px] font-semibold " + (draft.mode === mode ? "border-blue-600 bg-blue-600 text-white" : "bg-white text-gray-600")}>{modeLabel(mode)}</button>)}</div>
            {draft.mode === "periodic" && <>
              <label className="text-[11px] font-medium text-gray-600">ON/OFF interval in minutes</label>
              <input type="number" min="1" max="1440" value={draft.intervalMinutes} onChange={(event) => setDraft({ ...draft, intervalMinutes: Number(event.target.value) })} className="w-full border rounded-lg px-3 py-2 text-[12px]" />
              <label className="text-[11px] font-medium text-gray-600">Starts at</label>
              <input type="datetime-local" value={draft.startAt} onChange={(event) => setDraft({ ...draft, startAt: event.target.value })} className="w-full border rounded-lg px-3 py-2 text-[12px]" />
            </>}
            {draft.mode === "time_range" && <>
              <label className="text-[11px] font-medium text-gray-600">Start</label>
              <input type="datetime-local" value={draft.startAt} onChange={(event) => setDraft({ ...draft, startAt: event.target.value })} className="w-full border rounded-lg px-3 py-2 text-[12px]" />
              <label className="text-[11px] font-medium text-gray-600">End</label>
              <input type="datetime-local" value={draft.endAt} onChange={(event) => setDraft({ ...draft, endAt: event.target.value })} className="w-full border rounded-lg px-3 py-2 text-[12px]" />
              <select value={draft.repeatMode} onChange={(event) => setDraft({ ...draft, repeatMode: event.target.value })} className="w-full border rounded-lg px-3 py-2 text-[12px]"><option value="none">Run once</option><option value="daily">Repeat every day</option><option value="weekly">Repeat every week</option></select>
            </>}
            <button disabled={!canControl || savingProgram} onClick={addProgram} className="w-full rounded-lg bg-blue-600 py-2 text-[12px] font-semibold text-white disabled:bg-gray-300">{savingProgram ? "Saving..." : "Save program"}</button>
            <button disabled={!canControl || resetting} onClick={resetSavedCommands} className="w-full rounded-lg border border-red-200 bg-red-50 py-2 text-[12px] font-semibold text-red-700 disabled:opacity-50">{resetting ? "Resetting..." : "Reset saved relay commands"}</button>
            <p className="text-[10px] leading-4 text-gray-400">Clears this device’s saved programs and persistent commands. It does not switch relays immediately or remove shared Product Auto/Schedule rules.</p>
          </div>
          <div className="bg-white border rounded-xl p-5">
            <h3 className="mb-1 text-[14px] font-semibold text-gray-800">Saved & configured automation</h3>
            <p className="mb-4 text-[11px] text-gray-500">All command sources affecting this device are shown below.</p>
            {loadingPrograms ? <p className="py-10 text-center text-[12px] text-gray-400">Loading automation...</p> : <div className="space-y-3">
              {programs.map((program) => <div key={program.id} className="flex items-center justify-between gap-4 rounded-lg border p-4">
                <div><p className="text-[12px] font-semibold text-gray-800">{program.name} <span className="ml-1 text-[10px] font-bold uppercase text-blue-600">{modeLabel(program.mode)}</span></p><p className="mt-1 text-[11px] text-gray-500">{programDescription(program, relayNames)}</p></div>
                <div className="flex gap-2"><button disabled={!canControl} onClick={() => setProgramEnabled(program, program.enabled === false)} className={"rounded-lg px-3 py-1.5 text-[11px] font-semibold " + (program.enabled === false ? "bg-gray-100 text-gray-500" : "bg-emerald-50 text-emerald-700")}>{program.enabled === false ? "Paused" : "Enabled"}</button><button disabled={!canControl} onClick={() => deleteProgram(program)} className="rounded-lg bg-red-50 px-3 py-1.5 text-[11px] font-semibold text-red-600">Delete</button></div>
              </div>)}
              {automation.states.map((state) => <div key={state.relay_key} className="rounded-lg border border-amber-100 bg-amber-50/50 p-4"><p className="text-[12px] font-semibold text-gray-800">{relayNames[norm(state.relay_key)] || state.relay_key} <span className="ml-1 text-[10px] font-bold uppercase text-amber-700">{state.always_on ? "Always On" : "Persistent state"}</span></p><p className="mt-1 text-[11px] text-gray-500">Saved target: {state.desired_state ? "ON" : "OFF"}</p></div>)}
              {automation.configuredRelays.map((rule) => <div key={rule.key} className="rounded-lg border border-blue-100 bg-blue-50/50 p-4"><p className="text-[12px] font-semibold text-gray-800">{rule.label || rule.key} <span className="ml-1 text-[10px] font-bold uppercase text-blue-700">{norm(rule.mode) === "auto" ? "Auto" : "Schedule"}</span></p><p className="mt-1 text-[11px] text-gray-500">{configuredRuleDescription(rule, relayNames)}</p></div>)}
              {!programs.length && !automation.states.length && !automation.configuredRelays.length && <p className="py-10 text-center text-[12px] text-gray-400">No saved or configured automation.</p>}
            </div>}
          </div>
        </div>
      )}
    </div>
  );
}
